import React, { useState } from 'react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Shield, 
  Clock, 
  FileText, 
  Wallet, 
  Music,
  CheckCircle,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';
import ClientRiskChecker from '../components/ClientRiskChecker';

const hubContent = [
  {
    id: 'late-payments',
    icon: Clock,
    title: 'Tips to Avoid Late Payments',
    image: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/bty21wqy_african-american-freelancer-listening-podcast-being-focused-online-work.jpg',
    tips: [
      {
        title: 'Set Clear Payment Terms Upfront',
        content: 'Always discuss and agree on payment terms before starting any work. Include due dates, accepted payment methods, and late payment fees in your contract.'
      },
      {
        title: 'Send Invoices Immediately',
        content: 'Send your invoice as soon as the work is completed. The longer you wait, the less urgent the payment feels to your client.'
      },
      {
        title: 'Use Automatic Payment Reminders',
        content: 'Set up automated reminders to go out 7 days before, on the due date, and 3 days after if unpaid.'
      },
      {
        title: 'Make Payments Easy',
        content: 'Accept multiple payment methods including bank transfer, credit cards, and digital wallets. The easier it is to pay, the faster you get paid.'
      },
      {
        title: 'Build Relationships',
        content: 'Maintain good relationships with clients. A friendly follow-up call is often more effective than multiple emails.'
      }
    ]
  },
  {
    id: 'deposit-strategies',
    icon: Wallet,
    title: 'Deposit Strategies (30-50% Upfront)',
    image: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/89c3iajt_hands-putting-banknotes-envelope.jpg',
    tips: [
      {
        title: 'Standard 50% Deposit',
        content: 'For most freelance work, request 50% upfront and 50% on completion. This protects both parties and shows client commitment.'
      },
      {
        title: 'Milestone Payments',
        content: 'For larger projects, split payments into milestones: 30% upfront, 40% at midpoint, 30% on completion.'
      },
      {
        title: 'Non-Refundable Booking Fee',
        content: 'For events and performances, charge a non-refundable booking fee (typically 25-50%) that secures the date on your calendar.'
      },
      {
        title: 'Present Deposits as Industry Standard',
        content: 'Frame deposits as "standard practice" rather than a trust issue. Most professionals in creative industries require upfront payment.'
      },
      {
        title: 'Deposit Protects Your Time',
        content: 'Explain that deposits protect your availability. Once a date is booked, you turn down other opportunities.'
      }
    ]
  },
  {
    id: 'dj-guidance',
    icon: Music,
    title: '"Paid Before Performance" for DJs',
    image: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/4oo4b5ez_Screenshot%202026-04-07%20at%2020.41.12.png',
    tips: [
      {
        title: 'Full Payment 7 Days Before',
        content: 'Require full payment at least 7 days before the event. This is industry standard and weeds out unreliable clients.'
      },
      {
        title: 'No Pay, No Play Policy',
        content: 'Be clear in your contract: if payment isn\'t received by the deadline, you\'re not obligated to perform and the deposit is forfeit.'
      },
      {
        title: 'Event-Day Payment Alternative',
        content: 'If clients prefer event-day payment, require it before you start setting up equipment, not after the performance.'
      },
      {
        title: 'Protect Against Last-Minute Cancellations',
        content: 'Your contract should specify that cancellations within 14 days forfeit the full fee, as you\'ve likely turned down other bookings.'
      },
      {
        title: 'Equipment & Setup Time',
        content: 'Factor in equipment transportation and setup time in your pricing. Many DJs arrive 2-3 hours early.'
      }
    ]
  },
  {
    id: 'invoice-wording',
    icon: FileText,
    title: 'Invoice Wording Examples',
    image: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/mv3dc239_Screenshot%202026-04-07%20at%2020.45.24.png',
    examples: [
      {
        type: 'Payment Due Date',
        text: 'Payment is due within 14 days of the invoice date. After this period, a late fee of 5% will be applied to the outstanding balance.'
      },
      {
        type: 'Deposit Clause',
        text: 'A non-refundable deposit of 50% is required to secure the booking. The remaining balance is due 7 days before the scheduled event/service date.'
      },
      {
        type: 'Late Payment Fee',
        text: 'Invoices not paid within the agreed terms will incur a late payment fee of 2% per week on the outstanding amount.'
      },
      {
        type: 'Cancellation Policy',
        text: 'Cancellations made less than 14 days before the scheduled date will result in forfeiture of the full quoted amount. Cancellations made 14-30 days in advance will result in forfeiture of the deposit.'
      },
      {
        type: 'Scope Changes',
        text: 'Any changes to the agreed scope of work must be approved in writing and may result in additional charges. Additional services will be invoiced separately.'
      }
    ]
  },
  {
    id: 'contract-wording',
    icon: Shield,
    title: 'Simple Contract Wording',
    image: 'https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/c01iehne_Screenshot%202026-04-07%20at%2021.48.01.png',
    examples: [
      {
        type: 'Basic Service Agreement',
        text: 'This agreement confirms that [Your Business] will provide [specific service] for [Client Name] on [date] at [location] for the total fee of [amount]. A non-refundable deposit of [amount] is due upon signing, with the balance due [X days] before the service date.'
      },
      {
        type: 'Performance Rights',
        text: 'The client grants [Your Business] permission to use photographs, videos, or audio recordings from this engagement for promotional purposes unless otherwise specified in writing.'
      },
      {
        type: 'Liability Limitation',
        text: '[Your Business] will not be held liable for any damages, injuries, or losses occurring during or as a result of the service, except in cases of gross negligence.'
      },
      {
        type: 'Force Majeure',
        text: 'Neither party shall be liable for failure to perform due to circumstances beyond their reasonable control, including but not limited to: natural disasters, government actions, strikes, or pandemics.'
      }
    ]
  }
];

export default function ProtectionHubPage() {
  const [expandedItems, setExpandedItems] = useState(['late-payments']);

  return (
    <div className="min-h-screen bg-[#050507] pt-20" data-testid="protection-hub">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Education</span>
          <h1 className="font-clash text-4xl sm:text-5xl font-medium text-white mt-2 mb-4" data-testid="protection-hub-title">
            Payment Protection Hub
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Learn strategies to prevent late payments and protect your income. Tips, templates, and best practices for creative freelancers.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Accordion */}
          <div className="lg:col-span-2">
            <Accordion 
              type="multiple" 
              value={expandedItems}
              onValueChange={setExpandedItems}
              className="space-y-4"
            >
              {hubContent.map((section) => (
                <AccordionItem 
                  key={section.id} 
                  value={section.id}
                  className="bg-surface rounded-md border border-white/8 overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-md bg-[#FF4500]/10 flex items-center justify-center">
                        <section.icon className="w-5 h-5 text-[#FF4500]" />
                      </div>
                      <span className="font-clash text-lg font-medium text-white text-left">
                        {section.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    {/* Image */}
                    {section.image && (
                      <div 
                        className="h-48 rounded-md mb-6 bg-cover bg-center"
                        style={{ backgroundImage: `url(${section.image})` }}
                      />
                    )}

                    {/* Tips Format */}
                    {section.tips && (
                      <div className="space-y-4">
                        {section.tips.map((tip, index) => (
                          <div key={index} className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-white mb-1">{tip.title}</h4>
                              <p className="text-white/60 text-sm leading-relaxed">{tip.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Examples Format */}
                    {section.examples && (
                      <div className="space-y-4">
                        {section.examples.map((example, index) => (
                          <div key={index} className="bg-black/30 rounded-md p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-[#FF4500]" />
                              <span className="font-medium text-white text-sm">{example.type}</span>
                            </div>
                            <p className="text-white/70 text-sm leading-relaxed italic">
                              "{example.text}"
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-surface p-6 rounded-md border border-white/8 card-glow sticky top-24">
              <h3 className="font-clash text-lg font-medium text-white mb-4">Quick Tips</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-md bg-[#FF4500]/10 border border-[#FF4500]/20">
                  <AlertTriangle className="w-5 h-5 text-[#FF4500] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white/80 font-medium">Always get it in writing</p>
                    <p className="text-xs text-white/50 mt-1">Verbal agreements are hard to enforce. Email confirmation at minimum.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-md bg-[#10B981]/10 border border-[#10B981]/20">
                  <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white/80 font-medium">Follow up promptly</p>
                    <p className="text-xs text-white/50 mt-1">Send reminders on the due date, not weeks later.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-md bg-[#F59E0B]/10 border border-[#F59E0B]/20">
                  <Wallet className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white/80 font-medium">Deposits are standard</p>
                    <p className="text-xs text-white/50 mt-1">50% upfront is normal. Don't feel awkward asking.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="text-sm font-medium text-white mb-3">Need to collect now?</h4>
                <a 
                  href="/email-generator"
                  className="flex items-center justify-between p-3 rounded-md bg-[#FF4500] hover:bg-[#CC3700] transition-colors"
                >
                  <span className="text-white font-medium">Generate Collection Email</span>
                  <ChevronRight className="w-5 h-5 text-white" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Client Risk Checker */}
        <div className="mt-16 mb-12">
          <div className="bg-surface p-6 sm:p-8 rounded-md border border-white/8 card-glow">
            <ClientRiskChecker />
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 flex items-start gap-3 p-4 rounded-md bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-500/90 font-medium mb-1">Important Disclaimer</p>
            <p className="text-yellow-500/70 text-sm">
              This platform does not provide legal advice. The content provided here is for general educational purposes only. 
              We offer professionally worded payment requests aligned with common practices.
              For specific legal matters or contract disputes, please consult a qualified legal professional in your jurisdiction.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
